/* ============================================================================
 * Movatic WMS — Frontend shared hook | CesarFilho 932048016
 * Arquivo: src/lib/hooks/useStockScan.ts
 * ----------------------------------------------------------------------------
 * Lê o endpoint /inventory/scan/?q=... (atenção: SEM prefixar /api no path).
 *
 * POR QUÊ SEM /api?
 * - O cliente HTTP (http/axios) já tem baseURL = "/api" em produção
 *   (e "http://localhost:8000/api" no DEV). Se colocarmos "/api/..." aqui,
 *   vira "/api/api/..." na VPS. Então sempre use caminhos relativos ao
 *   recurso raiz: "/inventory/scan/", "/inventory/stock/", etc.
 *
 * SHAPE CANÔNICO (Desktop + Mobile)
 * - Retorna um objeto normalizado para ambos os apps usarem.
 * - Usa React Query (cache + revalidação).
 * - Aceita "enabled" para buscas sob demanda (ex.: botão Buscar).
 *
 * TOLERÂNCIA A VARIAÇÕES DO BACKEND
 * - O payload do backend pode trazer campos parciais; aplicamos defaults.
 * - Não repetimos chaves (evita TS2783 em builds).
 * ========================================================================= */

import { useQuery } from '@tanstack/react-query'
import { http } from '@/lib/http' // axios com baseURL já em /api

// ----------------------------- Tipos públicos --------------------------------

export type StockScan = {
  found: boolean
  query: string
  product?: {
    id: number
    code?: string
    ean?: string
    name?: string
    uom?: string
  }
  images: { url: string; alt?: string }[]
  total: { qty: number; uom?: string }
  locations: { location: string; qty: number }[]
  receiving?: {
    on_dock_qty?: number
    open_po?: { po: string; qty: number; eta?: string }[]
  }
}

/**
 * Forma esperada do payload do backend (todos opcionais).
 * Usamos Partial para aceitar respostas parciais sem quebrar o type-check.
 */
type StockScanResponse = Partial<{
  found: boolean
  query: string
  product: StockScan['product']
  images: StockScan['images']
  total: StockScan['total']
  locations: StockScan['locations']
  receiving: StockScan['receiving']
}>

// ------------------------ Normalização / mapeamento ---------------------------

/**
 * Normaliza o payload do backend para o shape canônico do app.
 * - Evita spreads com chaves repetidas.
 * - Aplica defaults para não quebrar a UI.
 */
function mapStockScan(raw: StockScanResponse, q: string): StockScan {
  // imagens sempre como array
  const images = Array.isArray(raw?.images) ? raw.images! : []

  // locations sempre como array
  const locations = Array.isArray(raw?.locations) ? raw.locations! : []

  // total com defaults
  const total =
    raw?.total && typeof raw.total.qty === 'number'
      ? { qty: raw.total.qty, uom: raw.total.uom ?? 'UN' }
      : { qty: 0, uom: 'UN' }

  // receiving com defaults
  const receiving = {
    on_dock_qty:
      typeof raw?.receiving?.on_dock_qty === 'number'
        ? raw.receiving!.on_dock_qty
        : 0,
    open_po: Array.isArray(raw?.receiving?.open_po)
      ? raw.receiving!.open_po!
      : [],
  }

  return {
    found: Boolean(raw?.found),
    query: raw?.query ?? q,
    product: raw?.product,
    images,
    total,
    locations,
    receiving,
  }
}

// ------------------------------ Hook principal --------------------------------

/**
 * Hook de consulta de scan.
 * @param q       Código a consultar (string lida pelo teclado/câmera).
 * @param enabled Controle externo para buscas sob demanda (true por padrão).
 *
 * Observação: se precisar filtrar por empresa, acrescente { params: { company_id } }
 * no GET (mantivemos simples para não quebrar chamadas existentes).
 */
export function useStockScan(q: string, enabled = true) {
  return useQuery({
    queryKey: ['scan', q],
    enabled: enabled && !!q, // só consulta se houver código
    queryFn: async (): Promise<StockScan> => {
      // IMPORTANTE: não prefixar /api aqui. O http já aponta para /api.
      const resp = await http.get<StockScanResponse>('/inventory/scan/', {
        params: { q },
      })
      return mapStockScan(resp.data ?? {}, q)
    },
    // Evita refetch agressivo entre leituras consecutivas
    staleTime: 15_000,
    // Tratamento de erro fica com o caller (React Query passa para isError/error)
  })
}

